import React, { useCallback, useMemo } from 'react'
import Modal from 'shared/ui/Modal/Modal'
import Toast from 'shared/ui/Toast/Toast'
import { useSendMessageModal } from 'pages/Students/components/SendMessageModal/hooks/useSendMessageModal'
import StepSendMessageOne from 'pages/Students/components/SendMessageModal/steps/StepSendMessageOne/StepSendMessageOne'
import { ReactComponent as BellIcon } from 'assets/icons/bell.svg'
import { sendMessagesToStudents } from 'api/all'
import { nudgeStudentApplicationDocument } from 'api/applicationDocuments'
import { getNudgeDeadlineBanner } from './getNudgeDeadlineBanner'
import s from './NudgeStudentModal.module.scss'

const BANNER_MOD = {
	neutral: s.bannerNeutral,
	ok: s.bannerOk,
	soon: s.bannerSoon,
	overdue: s.bannerOverdue,
}

const NudgeStudentModal = ({
	isOpen,
	onClose,
	studentId,
	studentName = 'Student',
	documentTitle = 'Document',
	requirementId,
	applicationDeadlineRaw = '',
	selectedParents = [],
	onSuccess,
}) => {
	// const [askKodaHint, setAskKodaHint] = useState('')

	// useEffect(() => {
	// 	if (isOpen) setAskKodaHint('')
	// }, [isOpen])

	const selectedStudents = useMemo(() => {
		const sid = String(studentId || '').trim()
		if (!sid) return []
		return [{ id: sid, name: studentName || 'Student' }]
	}, [studentId, studentName])

	const initialMessage = useMemo(
		() =>
			`Hi {{student_name}},\n\nThis is a friendly reminder to upload "${documentTitle}" for your application. If you have questions, just reply here.\n\nThank you!`,
		[documentTitle],
	)

	const banner = useMemo(
		() => getNudgeDeadlineBanner(applicationDeadlineRaw),
		[applicationDeadlineRaw],
	)

	const customSend = useCallback(
		async ({ message, studentIds }) => {
			const rid = String(requirementId || '').trim()
			if (!rid) throw new Error('Missing document requirement.')
			await nudgeStudentApplicationDocument(studentId, rid)
			await sendMessagesToStudents({
				message,
				student_ids: studentIds,
			})
		},
		[studentId, requirementId],
	)

	const {
		visibleStudents,
		hiddenStudentsCount,
		showAllStudents,
		toggleShowAllStudents,
		visibleParents,
		hiddenParentsCount,
		showAllParents,
		toggleShowAllParents,
		removeStudent,
		addParents,
		setAddParents,
		messageText,
		setMessageText,
		insertStudentName,
		insertParentName,
		close,
		footerActions,
		showToast,
		closeToast,
		sendError,
		successToastMessage,
	} = useSendMessageModal({
		isOpen,
		onClose,
		selectedStudents,
		selectedParents,
		initialMessage,
		customSend,
		successToastMessage: 'Reminder sent!',
		onAfterSendSuccess: async () => {
			const rid = String(requirementId || '').trim()
			await onSuccess?.(rid)
		},
	})

	const prepend = (
		<div className={s.info}>
			<div className={s.assignment}>
				<span className={s.assignment__label}>Assignment</span>
				<span className={s.assignment__value}>
					Missing Document Upload — {documentTitle}
				</span>
			</div>
			<div
				className={[s.banner, BANNER_MOD[banner.variant] || ''].join(' ')}
				role="status"
			>
				{banner.text}
			</div>
		</div>
	)

	return (
		<>
			<Modal
				isOpen={isOpen}
				onClose={close}
				title="Nudge Student"
				iconTitle={BellIcon}
				width={568}
				maxHeight={720}
				footerButtons={footerActions}
				ariaLabel="Nudge Student"
			>
				{sendError ? (
					<p
						style={{
							color: 'var(--color-red, #c00)',
							margin: '0 0 12px',
							fontSize: 14,
						}}
					>
						{sendError}
					</p>
				) : null}
				<StepSendMessageOne
					prependContent={prepend}
					hideRemoveStudent
					visibleStudents={visibleStudents}
					hiddenStudentsCount={hiddenStudentsCount}
					showAllStudents={showAllStudents}
					toggleShowAllStudents={toggleShowAllStudents}
					visibleParents={visibleParents}
					hiddenParentsCount={hiddenParentsCount}
					showAllParents={showAllParents}
					toggleShowAllParents={toggleShowAllParents}
					removeStudent={removeStudent}
					addParents={addParents}
					setAddParents={setAddParents}
					messageText={messageText}
					setMessageText={setMessageText}
					insertStudentName={insertStudentName}
					insertParentName={insertParentName}
				/>
			</Modal>
			<Toast
				isVisible={showToast}
				message={successToastMessage}
				onClose={closeToast}
			/>
		</>
	)
}

export default NudgeStudentModal
